// Push Notification Service
// Handles FCM initialization, permission requests, and token management

import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { getApp } from 'firebase/app';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

let messaging: Messaging | null = null;

// VAPID Key from Firebase Console > Cloud Messaging > Web Push certificates
const VAPID_KEY = 'BFExr3P4lJDYMe0cEYlRedn0ubMFuFrxi1rvtQgg5nMPZpn62OQUo7rfP1NROKjt3puZtACOtx67MOGCV8qCoYs';

/**
 * Initialize Firebase Cloud Messaging
 * Must be called after Firebase app is initialized
 */
export const initializeMessaging = (): Messaging | null => {
    if (typeof window === 'undefined') return null;

    // Check if browser supports notifications
    if (!('Notification' in window)) {
        console.warn('This browser does not support notifications');
        return null;
    }

    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
        console.warn('Service Workers are not supported');
        return null;
    }

    try {
        const app = getApp();
        messaging = getMessaging(app);
        return messaging;
    } catch (error) {
        console.error('Failed to initialize FCM:', error);
        return null;
    }
};

/**
 * Request notification permission from the user
 * Returns the FCM token if granted, null otherwise
 */
export const requestNotificationPermission = async (userId: string): Promise<string | null> => {
    try {
        // First, check/request browser permission
        const permission = await Notification.requestPermission();

        if (permission !== 'granted') {
            console.log('Notification permission denied');
            return null;
        }

        // Initialize messaging if not already done
        if (!messaging) {
            messaging = initializeMessaging();
            if (!messaging) return null;
        }

        // Register service worker and wait for it to be ready
        let registration: ServiceWorkerRegistration;

        // Check if already registered
        const existingRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');

        if (existingRegistration) {
            registration = existingRegistration;
            console.log('Using existing Service Worker registration');
        } else {
            registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            console.log('Service Worker registered');
        }

        // Wait for the service worker to be ready/active
        if (registration.installing) {
            console.log('Service Worker installing...');
            await new Promise<void>((resolve) => {
                registration.installing!.addEventListener('statechange', function handler(e) {
                    if ((e.target as ServiceWorker).state === 'activated') {
                        console.log('Service Worker activated');
                        resolve();
                    }
                });
            });
        } else if (registration.waiting) {
            console.log('Service Worker waiting...');
            await new Promise<void>((resolve) => {
                registration.waiting!.addEventListener('statechange', function handler(e) {
                    if ((e.target as ServiceWorker).state === 'activated') {
                        console.log('Service Worker activated');
                        resolve();
                    }
                });
            });
        } else if (registration.active) {
            console.log('Service Worker already active');
        }

        // Additional wait to ensure SW is fully ready
        await navigator.serviceWorker.ready;
        console.log('Service Worker ready');

        // Get FCM token
        const token = await getToken(messaging, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: registration
        });

        if (token) {
            console.log('FCM Token obtained:', token.substring(0, 20) + '...');

            // Save token to Firestore for this user
            await saveTokenToFirestore(userId, token);

            return token;
        } else {
            console.warn('No FCM token available');
            return null;
        }
    } catch (error) {
        console.error('Error requesting notification permission:', error);
        return null;
    }
};

/**
 * Save FCM token to Firestore for the user
 */
const saveTokenToFirestore = async (userId: string, token: string): Promise<void> => {
    try {
        // Detect platform for better token management
        const isAndroid = /android/i.test(navigator.userAgent);
        const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
        const isTWA = document.referrer.includes('android-app://') ||
                       (window.matchMedia('(display-mode: standalone)').matches && isAndroid);
        let platform = 'web';
        if (isTWA) platform = 'android-twa';
        else if (isAndroid) platform = 'android-web';
        else if (isIOS) platform = 'ios-web';

        const tokenRef = doc(db, 'fcmTokens', userId);
        await setDoc(tokenRef, {
            token,
            updatedAt: new Date().toISOString(),
            platform,
            userAgent: navigator.userAgent.substring(0, 200)
        }, { merge: true });
        console.log(`FCM Token saved to Firestore (platform: ${platform})`);
    } catch (error) {
        console.error('Error saving FCM token:', error);
    }
};

/**
 * Get user's FCM token from Firestore
 */
export const getUserFCMToken = async (userId: string): Promise<string | null> => {
    try {
        const tokenRef = doc(db, 'fcmTokens', userId);
        const tokenDoc = await getDoc(tokenRef);

        if (tokenDoc.exists()) {
            return tokenDoc.data().token;
        }
        return null;
    } catch (error) {
        console.error('Error getting FCM token:', error);
        return null;
    }
};

/**
 * Listen for foreground messages
 * Call this in your main App component
 */
export const onForegroundMessage = (callback: (payload: any) => void): (() => void) | null => {
    if (!messaging) {
        messaging = initializeMessaging();
    }

    if (!messaging) {
        console.warn('Messaging not initialized');
        return null;
    }

    return onMessage(messaging, (payload) => {
        console.log('Foreground message received:', payload);
        callback(payload);
    });
};

/**
 * Check if notifications are enabled for this browser/user
 */
export const areNotificationsEnabled = (): boolean => {
    if (!('Notification' in window)) return false;
    return Notification.permission === 'granted';
};

/**
 * Get current notification permission status
 */
export const getNotificationPermissionStatus = (): NotificationPermission | 'unsupported' => {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
};

/**
 * Refresh FCM token if notifications are already granted.
 * Should be called on every login/app launch to prevent stale tokens.
 * This is a silent operation — no permission prompt is shown.
 */
export const refreshTokenIfNeeded = async (userId: string): Promise<void> => {
    try {
        // Only refresh if permission was already granted
        if (!('Notification' in window) || Notification.permission !== 'granted') {
            return;
        }

        if (!('serviceWorker' in navigator)) {
            return;
        }

        // Initialize messaging if not already done
        if (!messaging) {
            messaging = initializeMessaging();
            if (!messaging) return;
        }

        // Get existing SW registration
        const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        if (!registration) {
            // No existing registration — will be set up when user enables notifications
            console.log('[FCM] No existing SW registration, skipping token refresh');
            return;
        }

        // Wait for SW to be ready
        await navigator.serviceWorker.ready;

        // Get current token (this may return a new token if the old one expired)
        const token = await getToken(messaging, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: registration
        });

        if (token) {
            // Save/update token in Firestore (will overwrite if changed)
            await saveTokenToFirestore(userId, token);
            console.log('[FCM] Token refreshed successfully');
        }
    } catch (error) {
        // Silent failure — don't break the app
        console.warn('[FCM] Token refresh failed (non-critical):', error);
    }
};
